import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  usePlotArea,
  useXAxisScale,
  useYAxisScale,
  XAxis,
  YAxis,
} from 'recharts';
import ClrChartTooltip from '../ClrChartTooltip';
import ClrRechartsTooltip from '../ClrRechartsTooltip';
import { CLR_MARK_HOVER_COLOR } from '../chartMarkHover';
import { buildNiceAxisScale, getSeriesMax } from '../axisScale';
import {
  categoryTickLabel,
  isCategoryPadPayload,
  padCategoriesToBottom,
} from '../padCategoryAxis';

const DOT_COLOR = '#26735C';
const AVG_LINE_COLOR = '#1F6F5C';
const GRID_COLOR = '#DBDEE4';
const DOT_RADIUS = 5.0083131790161135;
const MIN_CATEGORY_SLOTS = 5;

function TurnaroundDot({ cx, cy, payload }) {
  if (payload?.__pad || cx == null || cy == null) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={DOT_RADIUS}
      fill={DOT_COLOR}
    />
  );
}

TurnaroundDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  payload: PropTypes.object,
};

function formatTurnaroundDays(value) {
  const rounded = Math.round(Number(value) * 10) / 10;
  if (!Number.isFinite(rounded)) return '0';
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function medianDotCenterY(yScale, categories, plot) {
  if (!yScale || !plot) return null;
  const centers = categories
    .map((name) => yScale(name, { position: 'middle' }))
    .filter((y) => typeof y === 'number' && Number.isFinite(y));
  if (!centers.length) return null;
  if (centers.length < 2) {
    const only = centers[0];
    const below = only + 16;
    if (below <= plot.y + plot.height - DOT_RADIUS) return below;
    return Math.max(plot.y + DOT_RADIUS, only - 16);
  }

  const plotMid = plot.y + (plot.height / 2);
  let best = (centers[0] + centers[1]) / 2;
  let bestDist = Math.abs(best - plotMid);
  for (let i = 1; i < centers.length - 1; i += 1) {
    const mid = (centers[i] + centers[i + 1]) / 2;
    const dist = Math.abs(mid - plotMid);
    if (dist < bestDist) {
      best = mid;
      bestDist = dist;
    }
  }
  return best;
}

function MedianDot({ days, categories, portalRef }) {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  const plot = usePlotArea();
  const [open, setOpen] = useState(false);

  if (days == null || !xScale || !yScale || !plot) return null;

  const cx = xScale(days);
  const cy = medianDotCenterY(yScale, categories, plot);
  if (cx == null || cy == null) return null;

  const portalTarget = open ? portalRef.current : null;

  return (
    <g className="clr-turnaround-median-dot">
      <circle
        cx={cx}
        cy={cy}
        r={DOT_RADIUS}
        fill={open ? CLR_MARK_HOVER_COLOR : DOT_COLOR}
        pointerEvents="none"
      />
      <circle
        cx={cx}
        cy={cy}
        r={10}
        fill="transparent"
        style={{ cursor: 'pointer' }}
        onMouseEnter={(event) => {
          event.stopPropagation();
          portalRef.current?.classList.add('clr-turnaround-plot--median');
          setOpen(true);
        }}
        onMouseMove={(event) => {
          event.stopPropagation();
        }}
        onMouseLeave={() => {
          portalRef.current?.classList.remove('clr-turnaround-plot--median');
          setOpen(false);
        }}
      />
      {portalTarget
        ? createPortal(
          <div
            style={{
              position: 'absolute',
              left: cx,
              top: cy,
              zIndex: 30,
              pointerEvents: 'none',
            }}
          >
            <ClrChartTooltip
              active
              payload={[{ payload: { payer: 'Median', days } }]}
              formatter={() => ({
                title: 'Median',
                rows: [{
                  key: 'days',
                  label: 'Avg. days',
                  value: formatTurnaroundDays(days),
                }],
              })}
            />
          </div>,
          portalTarget,
        )
        : null}
    </g>
  );
}

MedianDot.propTypes = {
  days: PropTypes.number,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  portalRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
};

function AvgApprovalTurnaroundCard({ data }) {
  const chartData = useMemo(() => (data || []).map((row) => ({
    payer: row.payer_name || row.payerName || 'Unknown',
    days: Number(row.avg_approval_days ?? row.avgApprovalDays ?? 0),
  })), [data]);

  const plotData = useMemo(
    () => padCategoriesToBottom(chartData, {
      minSlots: MIN_CATEGORY_SLOTS,
      categoryKey: 'payer',
      valueKeys: ['days'],
    }),
    [chartData],
  );

  const meanDays = useMemo(() => {
    if (!chartData.length) return 0;
    const sum = chartData.reduce((acc, row) => acc + (Number(row.days) || 0), 0);
    return sum / chartData.length;
  }, [chartData]);

  const axis = useMemo(
    () => buildNiceAxisScale(getSeriesMax(chartData, ['days'])),
    [chartData],
  );
  const plotRef = useRef(null);
  const categoryNames = useMemo(() => plotData.map((row) => row.payer), [plotData]);

  return (
    <div className="clr-chart-card">
      <div className="clr-chart-card-header clr-chart-card-header-accent">
        <h3 className="clr-chart-card-title">Avg. approval turnaround time</h3>
      </div>
      <div className="clr-chart-card-body">
        {chartData.length === 0 ? (
          <p className="clr-chart-plot clr-chart-empty">No turnaround data</p>
        ) : (
          <div className="clr-chart-plot clr-turnaround-plot" ref={plotRef} style={{ position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid
                  stroke={GRID_COLOR}
                  strokeWidth={1}
                  strokeDasharray=""
                  horizontal
                  vertical
                  syncWithTicks
                />
                <XAxis
                  type="number"
                  dataKey="days"
                  domain={[0, axis.domainMax]}
                  ticks={axis.ticks}
                  height={24}
                  tickMargin={4}
                  tick={{ fontSize: 11, fill: '#6b7a74' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}d`}
                />
                <YAxis
                  type="category"
                  dataKey="payer"
                  width={90}
                  ticks={plotData.map((row) => row.payer)}
                  interval={0}
                  tick={{ fontSize: 11, fill: '#3e4f4c' }}
                  tickFormatter={categoryTickLabel}
                  axisLine={false}
                  tickLine={false}
                />
                {meanDays > 0 && (
                  <ReferenceLine
                    x={meanDays}
                    stroke={AVG_LINE_COLOR}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                    zIndex={100}
                  />
                )}
                <ClrRechartsTooltip
                  cursor={false}
                  formatter={(payload) => {
                    if (isCategoryPadPayload(payload)) return { title: null, rows: [] };
                    const row = payload[0]?.payload;
                    return {
                      title: row?.payer || '',
                      rows: [{
                        key: 'days',
                        label: 'Avg. days',
                        value: row?.days ?? 0,
                      }],
                    };
                  }}
                />
                <Scatter
                  data={plotData}
                  fill={DOT_COLOR}
                  shape={<TurnaroundDot />}
                  zIndex={300}
                />
                {meanDays > 0 && (
                  <MedianDot
                    days={meanDays}
                    categories={categoryNames}
                    portalRef={plotRef}
                  />
                )}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

AvgApprovalTurnaroundCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    payer_name: PropTypes.string,
    payerName: PropTypes.string,
    avg_approval_days: PropTypes.number,
    avgApprovalDays: PropTypes.number,
  })),
};

export default AvgApprovalTurnaroundCard;
